import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  ActionIcon,
  Button,
  Group,
  LoadingOverlay,
  Menu,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useDocumentTitle, useMounted } from "@mantine/hooks";
import {
  IconChevronDown,
  IconEye,
  IconPencil,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { EditModal as EditPrescriptionModal } from "@pages/Patients/Prescription/components/EditModal";

import { WithPatientNavbar } from "@layouts/WithPatientNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { CustomPagination } from "@components/CustomPagination";

import { truncateWithEllipsis } from "@utils/truncateText";

import { PAGE_TITLE, QP } from "@constants/index";

import { prescriptionQKeys } from "@hooks/queries/prescription/queryKeys";
import { useGetMedicationsForPrescription } from "@hooks/queries/prescription/useGetMedicationsForPrescription";
import { useGetSinglePrescription } from "@hooks/queries/prescription/useGetSinglePrescription";
import { useRefreshAnimation } from "@hooks/useRefreshAnimation";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";

import { Medication as IMedication } from "@customTypes/prescription";

import { StyledGeneralTab } from "../Prescription/components/GeneralTab/GeneralTab.styled";

import { AddModal } from "./components/AddModal";
import { TableControls } from "./components/TableControls";
import { StyledMedicationTable } from "./Medication.styled";


const columnHelper = createColumnHelper<Partial<IMedication>>();

export const Medication = () => {
  const { t, i18n } = useTranslation();

  useDocumentTitle(`${t("medication")} • ${PAGE_TITLE}`);
  const queryClient = useQueryClient();
  const { prescriptionId, patientId } = useParams();
  const { state } = useLocation();

  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({ searchFilter });
  const [tableData, setTableData] = useState<Partial<IMedication>[] | []>([]);

  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();
  const [prescriptionReadOnlyMode, setPrescriptionReadOnlyMode] =
    useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [
    editPrescriptionModalOpened,
    { open: editPrescriptionModalOpen, close: editPrescriptionModalClose },
  ] = useDisclosure(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { buttonRef } = useRefreshAnimation({ isRefreshing });

  const { data, isError, isSuccess, isLoading, fetchStatus, isRefetching } =
    useGetMedicationsForPrescription({
      prescriptionId: prescriptionId ?? "",
      patientId: patientId ?? "",
      pagination: {
        page: tablePagination.page,
        pageSize: tablePagination.pageSize,
      },
      search: debounced.toLocaleLowerCase(),
    });
  const { data: prescriptionData } = useGetSinglePrescription({
    prescriptionId: prescriptionId ?? "",
  });

  const medicationColumns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t("medication"),
        cell: info => {
          return (
            <p className="type-wrapper">
              {/* {info.row.original?.files?.length ? <GrAttachment /> : null} */}
              {truncateWithEllipsis(info.cell.getValue(), 20)}
            </p>
          );
        },
      }),
      columnHelper.accessor("frequency", {
        header: t("frequency"),
      }),
      columnHelper.accessor("dosage", {
        header: t("dosage"),
        cell: info => truncateWithEllipsis(info.cell.getValue(), 15),
      }),
      //   columnHelper.accessor("prescribedBy", {
      //     header: "Prescribed By",
      //     cell: info => truncateWithEllipsis(info.cell.getValue()?.fullName, 15),
      //   }),
      columnHelper.accessor("duration", {
        header: t("duration"),
      }),
      columnHelper.display({
        id: "action",
        header: isMobile ? t("action") : t("last-updated-action"),
        cell: info => {
          // console.log(info.row.original);
          return (
            <TableControls
              medication={info.row.original}
              cId={prescriptionData?.consultationInfo?.cId ?? ""}
            />
          );
        },
      }),
    ],
    [tableData, prescriptionData, i18n.language]
  );

  const table = useReactTable({
    data: tableData,
    columns: medicationColumns,
    // state: {
    //   globalFilter: searchFilter,
    // },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // globalFilterFn: (
    //   row: Row<Partial<IMedication>>,
    //   columnId: string,
    //   value: string
    // ) => {
    //   if (columnId === "name") {
    //     return filterFns.includesString(row, columnId, value, () => null);
    //   }
    //   return false;
    // },
  });

  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination(data.pagination);
    } else {
      setTableData([]);
    }
  }, [data]);

  useEffect(() => {
    if (setIsRefreshing) setIsRefreshing(isRefetching);
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);

  return (
    <WithSidebar hasPatientNavbar>
      <WithPatientNavbar>
        <StyledMedicationTable>
          <div className="header">
            <div className="back-btn-wrapper">
              <div className="breadcrumb">
                <Link to=".." className="back-btn hover-underline">
                  <span>{t("prescriptions")}</span>
                </Link>
                &gt;
                <Link to="#" className="detail">
                  <span>
                    {state?.selectedPrescription?.consultationInfo?.cId ??
                      prescriptionData?.consultationInfo?.cId ??
                      "..."}
                  </span>
                </Link>
              </div>

              <Group gap={"xs"}>
                <h2>{t("medications")}</h2>
                <Tooltip
                  label={t("refresh-data")}
                  position="right-end"
                  fz={12}
                  events={{ focus: true, hover: true, touch: true }}>
                  <ActionIcon
                    ref={buttonRef}
                    variant={"default"}
                    size={"md"}
                    onClick={() => {
                      queryClient.invalidateQueries(
                        prescriptionQKeys.medication(prescriptionId as string)
                      );
                    }}>
                    <IconRefresh
                      className={"refresh-icon"}
                      color="var(--mantine-color-black)"
                      size={16}
                    />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </div>

            <AddModal
              opened={opened}
              close={close}
              prescriptionId={prescriptionId ?? ""}
              cId={
                state?.selectedPrescription?.consultationInfo?.cId ??
                prescriptionData?.consultationInfo?.cId ??
                ""
              }
            />

            <EditPrescriptionModal
              opened={editPrescriptionModalOpened}
              close={editPrescriptionModalClose}
              prescription={
                state?.selectedPrescription
                  ? state.selectedPrescription
                  : prescriptionData
              }
              readOnlyMode={prescriptionReadOnlyMode}
            />
            <Button.Group>
              <PermissionGate permission="medications.create"><Button leftSection={<IconPlus size={18} />} onClick={open}>
                {t("add-medication")}
              </Button></PermissionGate>

              <Menu withArrow position="bottom-end" withinPortal>
                <Menu.Target>
                  <Button
                    px="xs"
                    style={{ borderLeft: "1px solid rgb(255,255,255,0.5)" }}>
                    <IconChevronDown size={18} />
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconEye size={16} stroke={1.5} />}
                    onClick={() => {
                      setPrescriptionReadOnlyMode(true);
                      editPrescriptionModalOpen();
                    }}>
                    {t("view-prescription-details")}
                  </Menu.Item>
                  <PermissionGate permission="prescriptions.edit"><Menu.Item
                    leftSection={<IconPencil size={16} stroke={1.5} />}
                    onClick={() => {
                      setPrescriptionReadOnlyMode(false);
                      editPrescriptionModalOpen();
                    }}>
                    {t("edit-prescription")}
                  </Menu.Item></PermissionGate>
                </Menu.Dropdown>
              </Menu>
            </Button.Group>
          </div>

          <>
            <LoadingOverlay
              visible={isRefetching && !!data?.newData?.length && !isMounted}
              zIndex={1000}
              overlayProps={{ blur: 0.1, opacity: 0.6 }}
              loaderProps={{ display: "none" }}
            />
            <div className="filters-wrapper">
              <TextInput
                placeholder={t("search-for-record")}
                w={{ base: 300, md: 350, lg: 400 }}
                value={searchFilter}
                onChange={e =>
                  setSearchFilter(
                    e.currentTarget.value.toLocaleLowerCase(),
                    "replace"
                  )
                }
                leftSection={<IconSearch size={16} />}
                rightSection={
                  searchFilter && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => setSearchFilter(null)}>
                      <IconX size={16} />
                    </ActionIcon>
                  )
                }
              />
            </div>
            <StyledGeneralTab>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              {isSuccess && !data?.newData?.length && debounced ? (
                <tbody>
                  <tr className="helper-row">
                    <td colSpan={medicationColumns.length}>
                      <p>
                        {t("empty-results-for", {
                          searchFilter: `"${debounced}"`,
                        })}
                      </p>
                    </td>
                  </tr>
                </tbody>
              ) : null}

              {isSuccess && !data?.newData?.length && !debounced ? (
                <tbody>
                  <tr className="helper-row">
                    <td colSpan={medicationColumns.length}>
                      <p>{t("empty-medications-for-prescription")}</p>
                    </td>
                  </tr>
                </tbody>
              ) : null}

              {isLoading && fetchStatus === "fetching" && !tableData.length ? (
                <tbody>
                  <tr className="helper-row">
                    <td colSpan={medicationColumns.length}>
                      <p>{t("loading")}</p>
                    </td>
                  </tr>
                </tbody>
              ) : null}

              {isError && !data?.newData?.length ? (
                <tbody>
                  <tr className="helper-row">
                    <td colSpan={medicationColumns.length}>
                      <p>{t("encountered-error")}</p>
                    </td>
                  </tr>
                </tbody>
              ) : null}

              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </StyledGeneralTab>

            {(isLoading && fetchStatus === "fetching") || !data ? null : (
              <CustomPagination
                table={table}
                tablePagination={tablePagination}
                otherParams={{
                  ...(debounced && { [QP.SEARCH]: debounced }),
                }}
              />
            )}
          </>
        </StyledMedicationTable>
      </WithPatientNavbar>
    </WithSidebar>
  );
};
